'use client';
import { ReactNode, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './video.module.scss';
import { Text } from '@/ui-components/atoms/Text/Text';
import { IconVideoPlayButton } from '@/assets/icons/IconVideoPlayButton';
import { Modal } from '@/ui-components/molecules/Modal/Modal';
import { VideoPlayer } from './VideoPlayer';
import useViewportWidth from '@/hooks/useViewportWidth';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';

export interface VideoProps {
    poster?: string;
    fullSizePoster?: boolean;
    source?: string;
    variant?: 'circle' | 'square';
    altText?: string;
    className?: string;
    absoluteCentered?: boolean;
    title?: ReactNode;
    plainTextTitle?: string;
    description?: ReactNode;
    videoId?: string;
    handlePlayClick?: () => void;
    onFocus?: () => void;
    copyright?: string;
    transcript?: ReactNode;
    isModal?: boolean;
    isInlineVideo?: boolean;
    isEmbeddedDescription?: boolean;
    isVideoGroup?: boolean;
    aspectRatioPadding?: boolean;
    captionSrc?: string;
}
export function Video(props: VideoProps): React.JSX.Element {
    const [initiatePlayer, setInitiatePlayer] = useState(!!props.videoId);
    const [activeVideo, setActiveVideo] = useState(!props.videoId);
    const [openTranscriptOnModal, setOpenTranscriptOnModal] = useState(false);
    const effectiveIsModal = props.isModal || openTranscriptOnModal;
    const thumbnailUrl = `https://img.youtube.com/vi/${props.videoId}/hqdefault.jpg`;
    const viewportWidth = useViewportWidth();
    const isMobile = viewportWidth <= 576;
    const isTablet = viewportWidth <= 820;
    const maxHeight = 232;
    const calculatedHeight = isMobile ? Math.min(viewportWidth * 0.48, maxHeight) : 304;
    const width = props.variant === 'circle' ? calculatedHeight : (calculatedHeight / 9) * 16;
    const [isInlinePlaying, setIsInlinePlaying] = useState(false);
    const [modalAutoplay, setModalAutoplay] = useState(true);
    const suppressInlineAutoplayRef = useRef(false);

    const videoContainerStyle = props.fullSizePoster
        ? {}
        : props.absoluteCentered
          ? {}
          : props.isInlineVideo && !isTablet
            ? {
                  width: `819px`,
                  height: `460px`,
              }
            : {
                  width: `${width}px`,
                  height: `${calculatedHeight}px`,
              };

    const textContainerStyle = {
        width: `${width}px`,
    };
    const videoClassNames = cn(styles.container, props.className, {
        [styles.absoluteCentered]: props.absoluteCentered,
    });
    const videoContainerClassNames = cn(styles.videoContainer, {
        [styles.circle]: props.variant === 'circle',
        [styles.fullSizePoster]: props.fullSizePoster,
        [styles.isModalVideo]: props.isModal,
        [styles.aspectRatioPadding]: props.aspectRatioPadding,
    });
    const textContainerClassNames = cn(styles.textContainer, {
        [styles.circle]: props.variant === 'circle',
        [styles.videoGroup]: props.isVideoGroup,
    });

    const altText = props.altText || props.plainTextTitle;

    const accessibleLabel = `Play video: ${altText || ''}`;
    const [syncTime, setSyncTime] = useState(0);
    const [currentVideoTime, setCurrentVideoTime] = useState(0);
    const handleVideoTimeSync = (time: number) => {
        setCurrentVideoTime(time);
    };
    const handleInitiatePlayButtonClick = () => {
        if (props.handlePlayClick) {
            props.handlePlayClick();
        } else {
            setInitiatePlayer(true);
            setActiveVideo(true);
        }
    };
    const handleCloseModal = () => {
        setSyncTime(currentVideoTime);
        setInitiatePlayer(false);
        setOpenTranscriptOnModal(false);
    };
    const handleTranscriptToggle = (shouldOpen: boolean) => {
        setSyncTime(currentVideoTime);

        if (shouldOpen) {
            setModalAutoplay(isInlinePlaying);
            suppressInlineAutoplayRef.current = !isInlinePlaying;
            setOpenTranscriptOnModal(true);
            setInitiatePlayer(true);
        } else {
            setOpenTranscriptOnModal(false);
        }
    };
    return (
        <>
            {effectiveIsModal && initiatePlayer && (
                <Modal
                    variant="video"
                    maxWidth={1024}
                    backgroundTransparent={true}
                    open={initiatePlayer}
                    onClose={handleCloseModal}
                    dataLocalpagename={props.videoId ? 'overlay:youtubevideo' : 'overlay:hostedvideo'}
                    contentClassName={styles.inlineVideo}
                    keepContentsMounted={!!props.videoId}
                >
                    <VideoPlayer
                        {...props}
                        description={props.isEmbeddedDescription && props.description}
                        initiatePlayer={initiatePlayer}
                        resumeFromLastTime
                        defaultTranscriptOpen={openTranscriptOnModal}
                        onTranscriptToggle={handleTranscriptToggle}
                        onTimeSync={handleVideoTimeSync}
                        syncTime={syncTime}
                        currentVideoTime={currentVideoTime}
                        isModalVideo={true}
                        activeVideo={activeVideo}
                        isInlineVideo={false}
                        autoplay={modalAutoplay}
                    />
                </Modal>
            )}
            <div className={videoClassNames} onFocus={props?.onFocus}>
                {!initiatePlayer || effectiveIsModal || (!!props.videoId && !activeVideo) ? (
                    <button
                        className={videoContainerClassNames}
                        tabIndex={0}
                        style={videoContainerStyle}
                        onClick={handleInitiatePlayButtonClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleInitiatePlayButtonClick();
                            }
                        }}
                        aria-label={accessibleLabel}
                    >
                        <ImageWrapper
                            src={props.poster || thumbnailUrl}
                            alt={altText || 'Video thumbnail'}
                            loadingStrategy="lazy"
                        />
                        <div className={styles.playButton}>
                            <IconVideoPlayButton />
                        </div>
                    </button>
                ) : (
                    <VideoPlayer
                        {...props}
                        description={props.isEmbeddedDescription && props.description}
                        initiatePlayer={initiatePlayer}
                        className={styles.videoPlayer}
                        resumeFromLastTime
                        defaultTranscriptOpen={openTranscriptOnModal}
                        onTranscriptToggle={handleTranscriptToggle}
                        isInlineVideo={props.isInlineVideo}
                        onTimeSync={handleVideoTimeSync}
                        syncTime={syncTime}
                        currentVideoTime={currentVideoTime}
                        isModalVideo={false}
                        activeVideo={!openTranscriptOnModal || (!!props.videoId && activeVideo)}
                        autoplay={suppressInlineAutoplayRef.current ? false : undefined}
                        dataComponentType={'inline video'}
                        onPlayingChange={setIsInlinePlaying}
                    />
                )}
                {!props.isInlineVideo && (
                    <button
                        className={textContainerClassNames}
                        style={textContainerStyle}
                        onClick={handleInitiatePlayButtonClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleInitiatePlayButtonClick();
                            }
                        }}
                    >
                        {props?.title && (
                            <Text className={styles.title} tag="h3">
                                {props?.title}
                            </Text>
                        )}
                        {props?.description && (
                            <Text className={styles.description} tag="p">
                                {props?.description}
                            </Text>
                        )}
                        {props?.copyright && <div className={styles.copyright}>{props?.copyright}</div>}
                    </button>
                )}
            </div>
        </>
    );
}
